import React, { Fragment, useState, useEffect } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { reduxForm, getFormValues, Field } from 'redux-form';

import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

import {
    SelectInput,
    TextInput,
    SwitchInput,
    AddButton,
    CopyButton,
    DeleteButton,
    CardElement,
    Dragable,
    Modal,
    StatusChip,
    EmptyState,
} from '../../_Components';
import { GET_ONE, provider } from '../../_utils';

const toCrmFields = (payload) => {
    if (!payload || payload.aborted || !payload.data) return [];
    return Object.keys(payload.data).map((key) => {
        const item = payload.data[key] || {};
        return { id: key, name: item.title !== key ? item.title : item.formLabel };
    });
};

const TypeOptions = [
    { id: 'bitrix', name: 'Bitrix' },
    { id: 'email', name: 'E-mail' },
];

const ActionOptions = [
    { id: 'create_task', name: 'Создать задачу' },
    { id: 'create_deal', name: 'Создать сделку' },
];

const SYSTEM_FIELDS = [
    { id: 'task_label', name: 'Тема звонка' },
    { id: 'sub_task_label', name: 'Подтема звонка' },
    { id: 'subtask_label', name: 'Подтема звонка (subtask_label)' },
    { id: 'task_value', name: 'Значение темы' },
    { id: 'service_value', name: 'Сервисное значение' },
    { id: 'model_label', name: 'Модель (название)' },
];

const makeId = () => {
    const time = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
    const rand = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    return `${time}${rand}`.slice(0, 24);
};

const fieldOptions = (projectFields) => ([
    ...SYSTEM_FIELDS,
    ...(projectFields || []).map((item) => ({ id: item.value, name: item.label })),
]);

const emptyBitrixAction = (action) => ({
    action: action || 'create_task',
    description: '',
    value: 'allways',
    deadline: action === 'create_deal' ? undefined : 30,
    category: action === 'create_deal' ? '' : undefined,
    fields: [],
    group: { value: false, field: '' },
    names: [],
});

const emptyEmailAction = () => ({
    group: { value: false },
    fields: [],
    names: [],
    subject: 'task_label',
});

const emptyProcessing = (type) => ({
    _id: makeId(),
    name: type === 'email' ? 'Новое письмо' : 'Новая обработка',
    type: type || 'bitrix',
    emails: [],
    actions: [type === 'email' ? emptyEmailAction() : emptyBitrixAction('create_task')],
});

const cloneProcessing = (item) => {
    const copy = JSON.parse(JSON.stringify(item || {}));
    copy._id = makeId();
    copy.name = `${item && item.name ? item.name : 'Обработка'} (копия)`;
    (copy.actions || []).forEach((action) => {
        action._id = makeId();
        (action.fields || []).forEach((field) => { field._id = makeId(); });
        (action.names || []).forEach((name) => { name._id = makeId(); });
    });
    return copy;
};

const typeLabel = (type) => (type === 'email' ? 'E-mail' : 'Bitrix');

const actionSummary = (item) => {
    if (!item) return '';
    if (item.type === 'email') {
        const count = (item.emails || []).length;
        return count ? `${count} адр.` : 'без адресов';
    }
    const kinds = (item.actions || []).map((action) => action.action).filter(Boolean);
    if (kinds.includes('create_deal') && kinds.includes('create_task')) return 'сделка + задача';
    if (kinds.includes('create_deal')) return 'сделка';
    if (kinds.includes('create_task')) return 'задача';
    return 'без действия';
};

const EmailsInput = ({ input: { value, onChange } }) => {
    const list = Array.isArray(value) ? value : [];
    const [draft, setDraft] = useState('');

    const add = () => {
        const email = draft.trim();
        if (!email || list.indexOf(email) >= 0) return;
        onChange(list.concat([email]));
        setDraft('');
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="name@company.by"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            add();
                        }
                    }}
                />
                <AddButton label="Добавить" handleAddClick={add} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {list.map((email, index) => (
                    <Chip
                        key={`${email}-${index}`}
                        size="small"
                        label={email}
                        variant="outlined"
                        onDelete={() => onChange(list.filter((_, i) => i !== index))}
                    />
                ))}
            </div>
        </div>
    );
};

const NewProcessingForm = () => (
    <div className="pc-fgrid">
        <Field name="name" required={true} component={TextInput} label="Название" />
        <Field name="type" required={true} component={SelectInput} options={TypeOptions} label="Тип" />
    </div>
);

const emailRows = (fields, options, path) => (
    (fields || []).map((item, key) => ({
        key: key + 1,
        fieldComponent: (
            <Field
                name={`${path}[${key}].field`}
                component={TextInput}
                style={{ width: '100%' }}
            />
        ),
        valueComponent: (
            <Field
                name={`${path}[${key}].value`}
                component={SelectInput}
                options={options}
                style={{ width: '100%' }}
            />
        ),
        ...item,
    }))
);

const emailHeader = [
    { label: 'Подпись в письме', width: '40%', value: 'fieldComponent' },
    { label: 'Значение из поля', width: '50%', value: 'valueComponent' },
    { label: '', width: '10%' },
];

const BitrixAction = ({ path, action, options, dealFields, change }) => {
    const fields = action.fields || [];
    const names = action.names || [];

    const addMapping = () => change(`${path}.fields`, fields.concat([{ card: '', crm: '', default: '' }]));
    const removeMapping = (index) => change(`${path}.fields`, fields.filter((_, i) => i !== index));
    const addName = () => change(`${path}.names`, names.concat([{
        field: '',
        value: '',
        sizeX: 2,
        sizeY: 1,
        row: 0,
        col: names.length * 2,
        delete: true,
        isField: true,
        edit: true,
    }]));
    const removeName = (index) => change(`${path}.names`, names.filter((_, i) => i !== index));

    return (
        <div className="pc-sect" style={{ borderTop: '1px solid var(--line-2)' }}>
            <div className="pc-fgrid pc-fgrid--3">
                <Field
                    name={`${path}.action`}
                    component={SelectInput}
                    label="Действие в CRM"
                    options={ActionOptions}
                />
                {action.action === 'create_task' && (
                    <Field
                        name={`${path}.deadline`}
                        component={TextInput}
                        label="Срок, минут"
                        mono={true}
                    />
                )}
                {action.action === 'create_deal' && (
                    <Field
                        name={`${path}.category`}
                        component={TextInput}
                        label="Воронка (category)"
                        mono={true}
                    />
                )}
                {action.action === 'create_task' && (
                    <Field
                        name={`${path}.description`}
                        component={SelectInput}
                        label="Комментарий из поля"
                        options={options}
                    />
                )}
            </div>
            {action.action === 'create_task' && (
                <div className="pc-togrow" style={{ marginTop: 10 }}>
                    <div className="pc-togrow__txt">
                        <b>Задача на группу</b>
                        <span>Исполнитель берётся из поля карточки.</span>
                    </div>
                    <Field name={`${path}.group.value`} component={SwitchInput} />
                </div>
            )}
            {action.action === 'create_task' && action.group && action.group.value && (
                <div className="pc-fgrid" style={{ marginTop: 8 }}>
                    <Field
                        name={`${path}.group.field`}
                        component={SelectInput}
                        label="Поле с группой"
                        options={options}
                    />
                </div>
            )}

            <div className="pc-sect__t" style={{ marginTop: 14 }}>Заголовок</div>
            {(names || []).map((name, index) => (
                <div key={index} className="pc-fgrid pc-fgrid--3" style={{ marginBottom: 8, alignItems: 'end' }}>
                    <Field
                        name={`${path}.names[${index}].field`}
                        component={SelectInput}
                        label="Поле"
                        options={options}
                    />
                    <Field
                        name={`${path}.names[${index}].value`}
                        component={TextInput}
                        label="Или фиксированный текст"
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 4 }}>
                        <IconButton size="small" onClick={() => removeName(index)}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </div>
                </div>
            ))}
            <AddButton handleAddClick={addName} label="Добавить часть заголовка" />

            {action.action === 'create_deal' && (
                <>
                    <div className="pc-sect__t" style={{ marginTop: 16 }}>Поля сделки</div>
                    {fields.map((row, index) => (
                        <div key={index} className="pc-fgrid pc-fgrid--3" style={{ marginBottom: 8, alignItems: 'end' }}>
                            <Field
                                name={`${path}.fields[${index}].card`}
                                component={SelectInput}
                                label="Поле карточки"
                                options={options}
                            />
                            <Field
                                name={`${path}.fields[${index}].crm`}
                                component={SelectInput}
                                label="Поле в CRM"
                                options={dealFields || []}
                                showValue
                            />
                            <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
                                <div style={{ flex: 1 }}>
                                    <Field
                                        name={`${path}.fields[${index}].default`}
                                        component={TextInput}
                                        label="По умолчанию"
                                    />
                                </div>
                                <IconButton size="small" onClick={() => removeMapping(index)} style={{ marginBottom: 4 }}>
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </div>
                        </div>
                    ))}
                    <AddButton handleAddClick={addMapping} label="Добавить поле" />
                </>
            )}
        </div>
    );
};

const Processing = ({ formValues, project, change }) => {
    const processing = (formValues && formValues.processing) || [];
    const [activeNodekey, setActiveNodekey] = useState(processing.length > 0 ? 0 : null);
    const [open, setOpen] = useState(false);
    const [dealFields, setDealFields] = useState(null);
    const projectId = project && project._id;
    const bitrix = !!(project && project.settings && project.settings.integration && project.settings.integration.type === 'Bitrix');

    useEffect(() => {
        if (!projectId || !bitrix) return undefined;
        let alive = true;
        provider(GET_ONE, '/bitrix/fields/deal', projectId).then((payload) => {
            if (!alive) return;
            setDealFields(toCrmFields(payload));
        });
        return () => { alive = false; };
    }, [projectId, bitrix]);
    const projectFields = (project && project.fields) || [];
    const options = fieldOptions(projectFields);
    const current = activeNodekey != null ? processing[activeNodekey] : null;
    const currentPath = `processing[${activeNodekey}]`;

    const addProcessing = (form) => {
        const next = [...processing, emptyProcessing(form && form.type)];
        if (form && form.name) next[next.length - 1].name = form.name;
        change('processing', next);
        setActiveNodekey(next.length - 1);
        setOpen(false);
    };

    const copyProcessing = (item) => {
        const next = [...processing, cloneProcessing(item)];
        change('processing', next);
        setActiveNodekey(next.length - 1);
    };

    const removeProcessing = (index) => {
        const item = processing[index];
        const id = item && (item._id || item.id);
        const next = processing.filter((_, i) => i !== index);
        change('processing', next);
        if (id && formValues && formValues.actions) {
            change('actions', formValues.actions.map((action) => {
                const settings = action.settings || {};
                if (`${settings.transfer_success}` !== `${id}` && `${settings.transfer_fail}` !== `${id}`) {
                    return action;
                }
                return {
                    ...action,
                    settings: {
                        ...settings,
                        transfer_success: `${settings.transfer_success}` === `${id}` ? null : settings.transfer_success,
                        transfer_fail: `${settings.transfer_fail}` === `${id}` ? null : settings.transfer_fail,
                    },
                };
            }));
        }
        setActiveNodekey(next.length ? Math.min(index, next.length - 1) : null);
    };

    const addBitrixAction = () => {
        const actions = (current && current.actions) || [];
        change(`${currentPath}.actions`, actions.concat([emptyBitrixAction('create_deal')]));
    };

    const removeBitrixAction = (index) => {
        const actions = (current && current.actions) || [];
        change(`${currentPath}.actions`, actions.filter((_, i) => i !== index));
    };

    const sortEmailFields = (fields) => {
        change(`${currentPath}.actions[0].fields`, (fields || []).map((row) => ({
            _id: row._id,
            field: row.field,
            value: row.value,
        })));
    };

    const removeEmailField = (key) => {
        const fields = (current && current.actions && current.actions[0] && current.actions[0].fields) || [];
        change(`${currentPath}.actions[0].fields`, fields.filter((_, i) => i !== key));
    };

    const addEmailField = () => {
        const fields = (current && current.actions && current.actions[0] && current.actions[0].fields) || [];
        change(`${currentPath}.actions[0].fields`, fields.concat([{ field: '', value: '' }]));
    };

    return (
        <div className="pc-fill">
            <div className="pc-split pc-split--tpl">
                <CardElement
                    fill
                    title="Обработка"
                    subheader={<StatusChip tone="mono">{processing.length}</StatusChip>}
                    action={<AddButton handleAddClick={() => setOpen(true)} label="Новая" variant="contained" />}
                    content={(
                        <div className="pc-panel-body">
                            <div className="pc-tblwrap">
                                {processing.map((item, index) => (
                                    <button
                                        key={item._id || index}
                                        type="button"
                                        className={`pc-listbtn${index === activeNodekey ? ' is-cur' : ''}`}
                                        onClick={() => setActiveNodekey(index)}
                                    >
                                        <StatusChip tone={item.type === 'email' ? 'acc' : 'ok'}>
                                            {typeLabel(item.type)}
                                        </StatusChip>
                                        <span className="pc-listbtn__t">{item.name || 'Без названия'}</span>
                                        <span className="pc-listbtn__n">{actionSummary(item)}</span>
                                    </button>
                                ))}
                                {processing.length === 0 && (
                                    <EmptyState
                                        title="Обработок нет"
                                        description="Создайте рецепт: письмо или действие в Bitrix после перевода."
                                    />
                                )}
                            </div>
                        </div>
                    )}
                />
                <CardElement
                    fill
                    title={current ? (current.name || 'Обработка') : 'Обработка'}
                    action={current ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <CopyButton label="Дублировать" onClick={() => copyProcessing(current)} />
                            <DeleteButton label="Удалить" onClick={() => removeProcessing(activeNodekey)} />
                        </div>
                    ) : null}
                    content={current ? (
                        <div className="pc-panel-body">
                            <div className="pc-tblwrap">
                                <div className="pc-sect" style={{ borderTop: 0 }}>
                                    <div className="pc-note pc-note--in">
                                        <InfoOutlinedIcon sx={{ fontSize: 16, mt: '1px' }} />
                                        <span>
                                            Рецепт запускается из действия: у перевода выберите его в «если состоялся» или «если не состоялся».
                                        </span>
                                    </div>
                                    <div className="pc-fgrid" style={{ marginTop: 12 }}>
                                        <Field
                                            name={`${currentPath}.name`}
                                            required={true}
                                            component={TextInput}
                                            label="Название"
                                        />
                                        <Field
                                            name={`${currentPath}.type`}
                                            required={true}
                                            component={SelectInput}
                                            options={TypeOptions}
                                            label="Тип"
                                        />
                                    </div>
                                </div>

                                {current.type === 'email' && (
                                    <>
                                        <div className="pc-sect">
                                            <div className="pc-sect__t">Кому отправить</div>
                                            <Field name={`${currentPath}.emails`} component={EmailsInput} />
                                        </div>
                                        <div className="pc-sect">
                                            <div className="pc-fgrid">
                                                <Field
                                                    name={`${currentPath}.actions[0].subject`}
                                                    component={SelectInput}
                                                    label="Тема письма из поля"
                                                    options={options}
                                                />
                                            </div>
                                        </div>
                                        <div className="pc-sect">
                                            <div className="pc-sect__t">Строки письма</div>
                                            <Dragable
                                                header={emailHeader}
                                                rows={emailRows(
                                                    (current.actions && current.actions[0] && current.actions[0].fields) || [],
                                                    options,
                                                    `${currentPath}.actions[0].fields`
                                                )}
                                                edit={false}
                                                sort={sortEmailFields}
                                                remove={true}
                                                removeFunc={removeEmailField}
                                            />
                                            <AddButton handleAddClick={addEmailField} label="Добавить строку" style={{ marginTop: 10 }} />
                                        </div>
                                    </>
                                )}

                                {current.type === 'bitrix' && (
                                    <>
                                        {(current.actions || []).map((action, index) => (
                                            <Fragment key={action._id || index}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 0 4px' }}>
                                                    <div className="pc-sect__t" style={{ margin: 0 }}>
                                                        {(ActionOptions.find((item) => item.id === action.action) || {}).name || `Действие ${index + 1}`}
                                                    </div>
                                                    {(current.actions || []).length > 1 && (
                                                        <IconButton size="small" onClick={() => removeBitrixAction(index)}>
                                                            <CloseIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </div>
                                                <BitrixAction
                                                    path={`${currentPath}.actions[${index}]`}
                                                    action={action}
                                                    options={options}
                                                    dealFields={dealFields}
                                                    change={change}
                                                />
                                            </Fragment>
                                        ))}
                                        <div style={{ padding: '10px 0 18px' }}>
                                            <AddButton handleAddClick={addBitrixAction} label="Добавить действие CRM" />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <EmptyState title="Обработка не выбрана" description="Выберите рецепт слева или создайте новый." />
                    )}
                />
            </div>
            {open && (
                <Modal
                    width={480}
                    title="Новая обработка"
                    open={open}
                    handleSaveClick={addProcessing}
                    handleCloseClick={() => setOpen(false)}
                    fields={<NewProcessingForm />}
                    initial={{ type: 'bitrix', name: '' }}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state) => {
    const formValues = getFormValues('card')(state);
    return { formValues };
};

export default compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'card',
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: true,
    }),
)(Processing);
